import React, { useCallback, useEffect, useState } from 'react';
import styles from '../../styles/ReporteNuevosIngresosStyles/ReporteNuevosIngresos.module.css';
import Sidebar, { useSidebar } from '../../components/Sidebar';
import HeaderGlobal from '../../components/HeaderGlobal';
import LoaderOverlay from '../../components/LoaderOverlay';
import FiltrosReporte from './FiltrosReporte';
import TablaReporte from './TablaReporte';
import { getReporteEstudiantesNuevoIngreso } from '../../api/reporteEstudiantesNuevoIngreso';
import { exportReporteNuevosIngresosXLS } from '../../utils/exportReporteNuevosIngresosXLS';
import { getCampus } from '../../api/campus';

const PAGE_SIZE = 10;

function ReporteNuevosIngresosScreen() {
  const { isCollapsed } = useSidebar();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState(null);
  const [contexto, setContexto] = useState({ ciclo: '', campus: '', seccion: '', programa: '' });
  const [campusList, setCampusList] = useState([]);
  const [page, setPage] = useState(1);

  const [filters, setFilters] = useState({
    id_ciclo_escolar: '',
    id_campus: '',
    id_seccion: '',
    id_programa: '',
  });

  // Cargar lista de campus una sola vez
  useEffect(() => {
    let mounted = true;
    getCampus().catch(() => []).then(cam => {
      if (mounted) setCampusList(Array.isArray(cam) ? cam : []);
    });
    return () => { mounted = false; };
  }, []);

  // Sincronizar id_campus con el header global
  useEffect(() => {
    if (!campusList.length) return;
    const resolve = (name) => {
      const found = campusList.find(c => c.nombre === name || String(c.id) === name);
      setFilters(prev => ({ ...prev, id_campus: found ? String(found.id) : '' }));
    };
    const handler = (e) => resolve((e.detail?.campus) || localStorage.getItem('selectedCampus') || '');
    window.addEventListener('campusChanged', handler);
    resolve(localStorage.getItem('selectedCampus') || '');
    return () => window.removeEventListener('campusChanged', handler);
  }, [campusList]);

  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  // Enriquecer el contexto con campus desde el header global
  const handleContextResolved = useCallback((ctx) => {
    const campusName = localStorage.getItem('selectedCampus') || '';
    const campusObj = campusList.find(c => c.nombre === campusName || String(c.id) === campusName);
    setContexto({ ...ctx, campus: campusObj ? campusObj.nombre : (campusName || 'Todos') });
  }, [campusList]);

  const fetchPage = useCallback(async (targetPage) => {
    if (!filters.id_ciclo_escolar) {
      setError('Selecciona un ciclo escolar para continuar.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await getReporteEstudiantesNuevoIngreso({
        id_ciclo_escolar: filters.id_ciclo_escolar,
        id_campus: filters.id_campus,
        id_seccion: filters.id_seccion,
        id_programa: filters.id_programa,
        page: targetPage,
        page_size: PAGE_SIZE,
      });
      setReportData(data);
      setPage(data.page || targetPage);
    } catch (err) {
      console.error('[ReporteNuevosIngresosScreen] Error:', err);
      setError(err.message || 'No se pudo obtener el reporte.');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const handleGenerate = useCallback(() => {
    setPage(1);
    fetchPage(1);
  }, [fetchPage]);

  const handlePageChange = useCallback((newPage) => {
    fetchPage(newPage);
  }, [fetchPage]);

  const handleExportXLS = useCallback(async () => {
    if (!reportData) return;
    setLoading(true);
    setError('');
    try {
      const all = await getReporteEstudiantesNuevoIngreso({
        id_ciclo_escolar: filters.id_ciclo_escolar,
        id_campus: filters.id_campus,
        id_seccion: filters.id_seccion,
        id_programa: filters.id_programa,
        all: true,
      });
      exportReporteNuevosIngresosXLS({
        resultados: all.resultados || [],
        contexto,
      });
    } catch (err) {
      console.error('[ReporteNuevosIngresosScreen] Export error:', err);
      setError(err.message || 'No se pudo exportar el reporte.');
    } finally {
      setLoading(false);
    }
  }, [reportData, filters, contexto]);

  const hasResults = reportData && Array.isArray(reportData.resultados) && reportData.resultados.length > 0;

  return (
    <div className={styles.mainContainer}>
      <div className={`${styles.layout} ${isCollapsed ? styles.sidebarCollapsedLayout : ''}`}>
        <div className={styles.sidebarColumn}>
          <Sidebar />
        </div>
        <div className={styles.contentColumn}>
          <main className={styles.mainContent}>
            <HeaderGlobal title="Reporte Estudiantes Nuevo Ingreso" />
            <div className={styles.contentArea}>
              <div className={styles.contentWrapper} style={{ position: 'relative' }}>
                <LoaderOverlay visible={loading} label="Generando reporte..." />

                <FiltrosReporte
                  filters={filters}
                  onChange={handleFilterChange}
                  onGenerate={handleGenerate}
                  loading={loading}
                  onContextResolved={handleContextResolved}
                />

                {error && (
                  <div
                    style={{
                      background: '#fdecea',
                      color: '#c5221f',
                      padding: '10px 14px',
                      borderRadius: 4,
                      marginBottom: 16,
                      fontSize: 13,
                    }}
                  >
                    {error}
                  </div>
                )}

                {reportData ? (
                  <div className={styles.reportContainer}>
                    <TablaReporte
                      resultados={reportData.resultados || []}
                      contexto={contexto}
                      page={reportData.page || page}
                      pageSize={reportData.page_size || PAGE_SIZE}
                      total={reportData.total || 0}
                      totalPages={reportData.total_pages || 1}
                      onPageChange={handlePageChange}
                    />
                    <div className={styles.actionsRow}>
                      <button
                        type="button"
                        className={styles.xlsButton}
                        onClick={handleExportXLS}
                        disabled={!hasResults || loading}
                        title={hasResults ? 'Exportar a XLSX' : 'No hay datos para exportar'}
                      >
                        <span role="img" aria-label="excel">📊</span>
                        Exportar XLS
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className={styles.reportContainer}>
                    <div className={styles.emptyState}>
                      <div className={styles.emptyIcon}>🆕</div>
                      <p className={styles.emptyText}>Sin reporte generado</p>
                      <p className={styles.emptySubText}>
                        Selecciona los filtros y presiona <strong>Generar Reporte</strong>.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

export default ReporteNuevosIngresosScreen;
